// frontend/src/components/UniversalAuditTool.jsx
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import LeftPanel from "./universal-audit/LeftPanel";
import { STORAGE_KEY } from "./universal-audit/constants";
import { buildAuditPayload } from "./universal-audit/buildAuditPayload";

/**
 * UNIVERSAL RISK AUDIT (Outside Paywall) — Inputs Only
 * Principles:
 * - Start with ZERO jurisdictions selected (no prepopulation)
 * - No output/preview on the input page (results page is the reveal)
 * - Currency pill only appears once jurisdiction(s) selected
 * - Disclaimer checkbox gates Calculate
 * - Engine runs ONLY on Calculate (no free what-if tuning)
 */

const DISCLAIMER_TEXT_VERSION = "universal_not_legal_advice_v1";

function currencyForJurKey(key) {
  switch (key) {
    case "UK":
      return { code: "GBP", symbol: "£" };
    case "EU":
      return { code: "EUR", symbol: "€" };
    case "US_FED":
    case "US_STATE":
      return { code: "USD", symbol: "$" };
    default:
      return null;
  }
}

function headlineCurrencyFromSelection(selectedJurKeys) {
  const keys = Array.from(selectedJurKeys || []);
  if (!keys.length) return null;
  if (keys.length === 1) return currencyForJurKey(keys[0]) || null;

  // Multiple jurisdictions selected → avoid implying FX conversion.
  return { code: "MIXED", symbol: "" };
}

export default function UniversalAuditTool({ importedSignal = null }) {
  const navigate = useNavigate();

  // =============================
  // INPUT STATE (UNIVERSAL)
  // =============================
  const [industry, setIndustry] = useState("");
  const [aiUseCase, setAiUseCase] = useState("");

  // Expanded headroom (Universal)
  const PEOPLE_MAX = 500000;
  const REV_MAX = 5000000000;

  // Start at zero (no prepopulation)
  const [peoplePerMonth, setPeoplePerMonth] = useState(0);
  const [annualRevenue, setAnnualRevenue] = useState(0);

  // Always comma-delimited display (text inputs)
  const [peopleText, setPeopleText] = useState("0");
  const [annualRevenueText, setAnnualRevenueText] = useState("0");

  // Jurisdictions: start EMPTY
  const [selectedJurKeys, setSelectedJurKeys] = useState(new Set());
  const [selectedStates, setSelectedStates] = useState(new Set());

  // Universal = conservative default, locked
  const usStateAggregationMode = "MAX";

  // Deterministic defaults (not shown in UI)
  const severityTier = 3;
  const enforcementInputs = {};
  const eventsSnapshot = null;
  const liveRiskMultiplier = 1.0;

  // REQUIRED disclaimer gating
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  // =============================
  // LOAD ENGINE (STATIC IMPORT)
  // =============================
  const runV3 = useMemo(() => {
    try {
      // keep require to avoid bundler issues in this codebase
      const mod = require("../lib/universalRiskEngine_v3.js");
      return mod.runUniversalRiskEngineV3 || mod.default;
    } catch {
      return null;
    }
  }, []);

  // =============================
  // Currency (UI only; no engine preview)
  // =============================
  const headlineCurrency = useMemo(() => {
    return headlineCurrencyFromSelection(selectedJurKeys);
  }, [selectedJurKeys]);

  // =============================
  // Text → numeric parsing (comma-delimited always)
  // =============================
  function normalizeDigitsToNumber(raw, max) {
    const digits = String(raw || "").replace(/[^\d]/g, "");
    const n = digits ? Math.min(Number(digits), max) : 0;
    return Number.isFinite(n) ? n : 0;
  }

  function onPeopleTextChange(e) {
    const n = normalizeDigitsToNumber(e.target.value, PEOPLE_MAX);
    setPeoplePerMonth(n);
    setPeopleText(n.toLocaleString("en-US"));
  }

  function onRevenueTextChange(e) {
    const n = normalizeDigitsToNumber(e.target.value, REV_MAX);
    setAnnualRevenue(n);
    setAnnualRevenueText(n.toLocaleString("en-US"));
  }

  // =============================
  // Jurisdiction toggles (ALLOW EMPTY)
  // Canonical keys: US_FED, US_STATE, EU, UK
  // =============================
  function toggleJur(k) {
    setSelectedJurKeys((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);

      // If US_STATE turned off, clear selected states
      if (!next.has("US_STATE")) {
        setSelectedStates(new Set());
      }
      return next;
    });
  }

  // =============================
  // Calculate gating reason
  // =============================
  const calculateDisabledReason = useMemo(() => {
    if (!runV3) return "Risk engine is not available (module load failed).";
    if (!selectedJurKeys || selectedJurKeys.size === 0) return "Select at least one jurisdiction to continue.";
    if (!disclaimerAccepted) return "Please acknowledge the disclaimer to continue.";
    return "";
  }, [runV3, selectedJurKeys, disclaimerAccepted]);

  // =============================
  // CALCULATE → RUN ENGINE ONCE → STORE → NAVIGATE
  // =============================
  function onCalculate() {
    if (!runV3) {
      window.alert("Risk engine is not available (module load failed).");
      return;
    }

    if (!selectedJurKeys || selectedJurKeys.size === 0) {
      window.alert("Please select at least one jurisdiction to continue.");
      return;
    }

    if (!disclaimerAccepted) {
      window.alert("Please acknowledge that this is not legal advice before calculating exposure.");
      return;
    }

    // Run v3 engine ONLY here (no preview on input page)
    const v3 = runV3({
      industry,
      niche: "", // Universal: niche removed
      aiUseCase,
      peoplePerMonth,
      annualRevenue,
      selectedJurKeys,
      selectedStates,
      usStateAggregationMode, // locked MAX
      severityTier,
      enforcementInputs,
      eventsSnapshot,
    });

    // Build payload (v3 adapter emitted inside buildAuditPayload)
    const payload = buildAuditPayload({
      // inputs
      industry,
      niche: "",
      aiUseCase,
      peoplePerMonth,
      annualRevenue,
      selectedJurKeys,
      selectedStates,
      usStateAggregationMode,

      // ✅ preferred engine output
      v3_engine_output: v3,

      // meta
      severityTier,
      enforcementInputs,
      eventsSnapshot,
      importedBanner: importedSignal,
      liveRiskMultiplier,
      ui_mode: "universal",

      // ✅ required disclaimer metadata (audit trail)
      disclaimer_acknowledged: true,
      disclaimer_text_version: DISCLAIMER_TEXT_VERSION,
    });

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      window.alert("Unable to store results (storage may be full). Please clear site data and retry.");
      return;
    }

    navigate("/universal-audit/results");
  }

  // =============================
  // RENDER (Inputs-only, full width)
  // =============================
  return (
    <div className="w-full">
      <LeftPanel
        // Business context
        industry={industry}
        setIndustry={setIndustry}
        aiUseCase={aiUseCase}
        setAiUseCase={setAiUseCase}
        niche={""}
        setNiche={() => {}}

        // Scale
        PEOPLE_MAX={PEOPLE_MAX}
        peoplePerMonth={peoplePerMonth}
        peopleText={peopleText}
        onPeopleTextChange={onPeopleTextChange}
        setPeopleFromSlider={(n) => {
          const v = Math.max(0, Math.min(PEOPLE_MAX, Number(n) || 0));
          setPeoplePerMonth(v);
          setPeopleText(v.toLocaleString("en-US"));
        }}

        REV_MAX={REV_MAX}
        annualRevenue={annualRevenue}
        annualRevenueText={annualRevenueText}
        onRevenueTextChange={onRevenueTextChange}
        setRevenueFromSlider={(n) => {
          const v = Math.max(0, Math.min(REV_MAX, Number(n) || 0));
          setAnnualRevenue(v);
          setAnnualRevenueText(v.toLocaleString("en-US"));
        }}

        // Jurisdictions + states
        selectedJurKeys={selectedJurKeys}
        toggleJur={toggleJur}
        shouldUseStates={selectedJurKeys.has("US_STATE")}
        selectedStates={selectedStates}
        setSelectedStates={setSelectedStates}
        toggleState={(code) =>
          setSelectedStates((prev) => {
            const next = new Set(prev);
            const c = String(code || "").toUpperCase();
            if (!c) return next;
            next.has(c) ? next.delete(c) : next.add(c);
            return next;
          })
        }
        clearStates={() => setSelectedStates(new Set())}

        // Currency (display-only; appears only after selection)
        headlineCurrency={headlineCurrency}

        // Disclaimer gating
        disclaimerAccepted={disclaimerAccepted}
        setDisclaimerAccepted={setDisclaimerAccepted}
        calculateDisabledReason={calculateDisabledReason}

        // CTA
        onCalculate={onCalculate}
      />
    </div>
  );
}